import { operators as o, common as c } from '@pendo/aggregations';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { applyCustomSegment } from './common.util';

type OnboardingEvent = {
    targetType: c.TargetType;
    targetId: c.TrackTypeId;
};

type TotalOnboardedAggregationParams = {
    appId: c.AppIdType;
    targetType?: c.TargetType;
    targetId?: c.TrackTypeId;
    onboardingEvents?: Record<string, OnboardingEvent>;
    firstComparisonRange: string;
    secondComparisonRange: string;
    thirdComparisonRange?: string;
    expectedDaysToOnboard: number;
    usesAccounts?: boolean;
    segmentId?: string;
};

export default {
    name: 'TotalOnboardedAggregation',
    productArea: PRODUCT_AREAS.IMPACT_BRIEF,
    subProductArea: SUB_PRODUCT_AREAS.ONBOARDING,
    responseFormat: 'row',
    build: ({
        appId,
        targetType,
        targetId,
        onboardingEvents,
        firstComparisonRange,
        secondComparisonRange,
        thirdComparisonRange,
        expectedDaysToOnboard,
        usesAccounts,
        segmentId,
    }: TotalOnboardedAggregationParams) => {
        if (!appId) {
            throw new Error('appId is required for TotalOnboardedAggregation');
        }
        if (!firstComparisonRange || !secondComparisonRange) {
            throw new Error('comparisonRange is required for TotalOnboardedAggregation');
        }

        return o.pipeline(
            o.spawn(
                buildBasePipeline(
                    {
                        appId,
                        targetType,
                        targetId,
                        onboardingEvents,
                        firstComparisonRange,
                        secondComparisonRange,
                        expectedDaysToOnboard,
                        usesAccounts,
                        segmentId,
                    },
                    false,
                    'totalNewFirstComparisonRange',
                ),
                buildBasePipeline(
                    {
                        appId,
                        targetType,
                        targetId,
                        onboardingEvents,
                        firstComparisonRange,
                        secondComparisonRange,
                        expectedDaysToOnboard,
                        usesAccounts,
                        segmentId,
                    },
                    true,
                    'totalNewSecondComparisonRange',
                ),
            ),
            o.join('id'),
            o.evalExpression({
                changePercentage:
                    'if(totalNewSecondComparisonRange == 0, 0, ((totalNewFirstComparisonRange - totalNewSecondComparisonRange) / totalNewSecondComparisonRange) * 100)',
                firstMonth: `formatTime("January 2006", ${thirdComparisonRange})`,
                lastMonth: `formatTime("January 2006", ${firstComparisonRange})`,
            }),
        );
    },
};

function buildBasePipeline (
    {
        appId,
        targetType,
        targetId,
        firstComparisonRange,
        secondComparisonRange,
        expectedDaysToOnboard,
        usesAccounts,
        segmentId,
    }: TotalOnboardedAggregationParams,
    isSecondComparison: boolean,
    reduceKey: string,
): c.PipelineData[] {
    const metadataAppPath = `_${(appId ?? '').toString().replace('-', '_')}`;
    const segmentFilter = isSecondComparison
        ? `firstTimeInApp >= ${secondComparisonRange} && firstTimeInApp < ${firstComparisonRange}`
        : `firstTimeInApp >= ${firstComparisonRange}`;

    const bulkExpandTarget = usesAccounts ? 'account' : 'visitor';
    const idType = usesAccounts ? 'accountId' : 'visitorId';
    const firstTimeInAppSegmentField = usesAccounts
        ? `metadata.auto${metadataAppPath}.firstvisit`
        : `if(isNil(metadata.auto${metadataAppPath}.firstidentifiedvisit), metadata.auto${metadataAppPath}.firstvisit, metadata.auto${metadataAppPath}.firstidentifiedvisit)`;
    const firstTimeInAppEvalField = usesAccounts
        ? `account.auto${metadataAppPath}.firstvisit`
        : `if(isNil(visitor.auto${metadataAppPath}.firstidentifiedvisit), visitor.auto${metadataAppPath}.firstvisit, visitor.auto${metadataAppPath}.firstidentifiedvisit)`;

    return o.pipeline(
        o.sources.timespan({ targetType, targetId }),
        o.identified('visitorId'),
        applyCustomSegment(
            o.pipeline(
                usesAccounts ? o.sources.accounts({ appId }) : o.sources.visitors({ appId }),
                usesAccounts ? [] : o.identified('visitorId'),
                o.select({
                    firstTimeInApp: firstTimeInAppSegmentField,
                    [idType]: idType,
                }),
                o.filter(segmentFilter),
            ),
            segmentId,
        ),
        usesAccounts ? o.group('accountId', o.groupField('firstTime', { min: 'firstTime' })) : [],
        o.bulkExpand(bulkExpandTarget, bulkExpandTarget, idType),
        o.evalExpression({
            firstTimeInApp: firstTimeInAppEvalField,
            timeBeforeConversion: 'dateDist(firstTimeInApp, firstTime, "daily")',
        }),
        o.filter(`timeBeforeConversion >= 0 && timeBeforeConversion <= ${expectedDaysToOnboard}`),
        o.reduce({
            [reduceKey]: o.count(idType),
        }),
        o.evalExpression({
            id: `"totalOnboardedAggregation_${targetId}"`,
        }),
    );
}
